import { getMerchantId } from "@/lib/company-storage"

export const PENDING_COUPON_STORAGE_KEY = "meucontroleai:pending-coupon"

export const COUPON_QUERY_PARAM = "cupom"

const MAX_CODE_LENGTH = 64

/** Fired on this window whenever the pending stash or the kept codes change. */
export const COUPON_STASH_EVENT = "meucontroleai:coupon-stash"

function announceStashChange(): void {
  if (typeof window === "undefined") return
  window.dispatchEvent(new Event(COUPON_STASH_EVENT))
}

export type PendingCoupon = {
  merchantId: string
  code: string
  /** Every code of a multi-coupon checkout; `code` stays the first one for older readers. */
  codes?: string[]
}

/** Uppercase + trim. Empty / oversized values become null. */
export function normalizeCouponCode(raw: string | null | undefined): string | null {
  if (!raw) return null
  const code = raw.trim().toUpperCase()
  if (!code || code.length > MAX_CODE_LENGTH) return null
  return code
}

/** Comparison key of a code, equal for the typed and the canonical form: case and hyphens ignored, as coupon-service-core resolves campaign codes. */
export function couponCodeKey(raw: string): string {
  return raw.toUpperCase().replace(/[-\s]/g, "")
}

export function readCouponQuery(search: string): string | null {
  try {
    return normalizeCouponCode(new URLSearchParams(search).get(COUPON_QUERY_PARAM))
  } catch {
    return null
  }
}

export function savePendingCoupon(code: string, merchantId: string): boolean {
  const normalized = normalizeCouponCode(code)
  if (!normalized || !merchantId) return false
  try {
    const payload: PendingCoupon = { merchantId, code: normalized }
    localStorage.setItem(PENDING_COUPON_STORAGE_KEY, JSON.stringify(payload))
    announceStashChange()
    return true
  } catch {
    return false
  }
}

/**
 * Returns the pending code only when it belongs to this merchant.
 * A leftover from another store is treated as absent.
 */
export function getPendingCoupon(merchantId: string): string | null {
  if (!merchantId) return null
  try {
    const raw = localStorage.getItem(PENDING_COUPON_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<PendingCoupon>
    if (parsed.merchantId !== merchantId) return null
    return normalizeCouponCode(parsed.code)
  } catch {
    return null
  }
}

/**
 * Stashes every code the customer typed, so leaving checkout to fix the bag
 * (the coupon blockers link back to it) does not lose them. An empty list
 * clears the stash.
 */
export function savePendingCouponCodes(codes: readonly string[], merchantId: string): boolean {
  const normalized = codes.map(normalizeCouponCode).filter((code): code is string => code !== null)
  if (!merchantId) return false
  if (normalized.length === 0) {
    clearPendingCoupon()
    return true
  }
  try {
    const payload: PendingCoupon = { merchantId, code: normalized[0], codes: normalized }
    localStorage.setItem(PENDING_COUPON_STORAGE_KEY, JSON.stringify(payload))
    announceStashChange()
    return true
  } catch {
    return false
  }
}

/** Every stashed code for this merchant, in the order they were typed. */
export function getPendingCouponCodes(merchantId: string): string[] {
  if (!merchantId) return []
  try {
    const raw = localStorage.getItem(PENDING_COUPON_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return []
    const stored: Partial<Record<keyof PendingCoupon, unknown>> = parsed
    if (stored.merchantId !== merchantId) return []
    const listed = Array.isArray(stored.codes) ? stored.codes : [stored.code]
    const codes = listed
      .map((code) => (typeof code === "string" ? normalizeCouponCode(code) : null))
      .filter((code): code is string => code !== null)
    return [...new Set(codes)]
  } catch {
    return []
  }
}

export function clearPendingCoupon(): void {
  try {
    localStorage.removeItem(PENDING_COUPON_STORAGE_KEY)
  } catch {
    return
  }
  announceStashChange()
}

/** Drops one code from the stash, keeping the others typed or received this visit. */
export function dropPendingCouponCode(code: string, merchantId: string): void {
  const normalized = normalizeCouponCode(code)
  if (!normalized || !merchantId) return
  const codes = getPendingCouponCodes(merchantId)
  if (codes.includes(normalized)) savePendingCouponCodes(codes.filter((entry) => entry !== normalized), merchantId)
}

export const KEPT_COUPONS_STORAGE_KEY = "meucontroleai:kept-coupons"

type KeptCoupons = { merchantId: string; codes: string[] }

function readKept(merchantId: string): string[] {
  if (!merchantId) return []
  try {
    const raw = localStorage.getItem(KEPT_COUPONS_STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== "object" || parsed === null) return []
    const stored: Partial<Record<keyof KeptCoupons, unknown>> = parsed
    if (stored.merchantId !== merchantId || !Array.isArray(stored.codes)) return []
    const codes = stored.codes
      .map((code) => (typeof code === "string" ? normalizeCouponCode(code) : null))
      .filter((code): code is string => code !== null)
    return [...new Set(codes)]
  } catch {
    return []
  }
}

function writeKept(merchantId: string, codes: string[]): void {
  try {
    if (codes.length === 0) localStorage.removeItem(KEPT_COUPONS_STORAGE_KEY)
    else localStorage.setItem(KEPT_COUPONS_STORAGE_KEY, JSON.stringify({ merchantId, codes } satisfies KeptCoupons))
  } catch {
    return
  }
  announceStashChange()
}

/**
 * Keeps a won or received code across visits — unlike the per-visit stash —
 * so a coupon that does not work today still reaches the checkout on the day
 * it does. It leaves when an order uses it or the server refuses it for good.
 */
export function keepCouponForLater(code: string, merchantId: string): boolean {
  const normalized = normalizeCouponCode(code)
  if (!normalized || !merchantId) return false
  const codes = readKept(merchantId)
  if (!codes.includes(normalized)) writeKept(merchantId, [...codes, normalized])
  return true
}

/** Codes kept for later at this merchant, oldest first. */
export function getKeptCouponCodes(merchantId: string): string[] {
  return readKept(merchantId)
}

/** Stops keeping these codes (used in an order, removed by the customer, or refused for good). */
export function forgetKeptCoupons(codes: readonly string[], merchantId: string): void {
  if (!merchantId || codes.length === 0) return
  const drop = new Set(codes.map(normalizeCouponCode).filter((code): code is string => code !== null))
  const kept = readKept(merchantId)
  const next = kept.filter((code) => !drop.has(code))
  if (next.length !== kept.length) writeKept(merchantId, next)
}

/** The codes applied to this visit's order at this merchant (the stash); the kept codes are the customer's wallet, applied only when picked. */
export function attachedCouponCodes(merchantId: string | null): string[] {
  if (!merchantId) return []
  return getPendingCouponCodes(merchantId)
}

/** Calls `listener` when this tab or another one changes the stash or the kept codes; returns the unsubscribe. */
export function subscribeCouponStash(listener: () => void): () => void {
  if (typeof window === "undefined") return () => undefined
  const controller = new AbortController()
  window.addEventListener(COUPON_STASH_EVENT, listener, { signal: controller.signal })
  window.addEventListener(
    "storage",
    (event) => {
      if (event.key === null || event.key === PENDING_COUPON_STORAGE_KEY || event.key === KEPT_COUPONS_STORAGE_KEY) listener()
    },
    { signal: controller.signal },
  )
  return () => controller.abort()
}

/** Persist `?cupom=` for the current merchant. Returns the saved code, or null. */
export function captureCouponFromUrl(search: string, merchantId = getMerchantId()): string | null {
  const code = readCouponQuery(search)
  if (!code || !merchantId) return null
  return savePendingCoupon(code, merchantId) ? code : null
}

let visitHydrated = false

/** Test seam: the next hydratePendingCouponForThisVisit runs as a fresh visit. */
export function resetPendingCouponHydrationForTests(): void {
  visitHydrated = false
}

/**
 * Once per page load: wipe any leftover pending coupon, then apply `?cupom=`
 * if the URL has one. No slug → stay cleared. Showcase taps after this
 * survive SPA navigation; they do not survive the next F5 unless `?cupom=`
 * is still in the URL.
 */
export function hydratePendingCouponForThisVisit(
  search: string,
  merchantId: string | null,
): string | null {
  if (visitHydrated) {
    return merchantId ? getPendingCoupon(merchantId) : null
  }
  if (!merchantId) return null
  visitHydrated = true
  clearPendingCoupon()
  return captureCouponFromUrl(search, merchantId)
}
